declare module 'highlight.js/lib/core';
declare module 'highlight.js/lib/languages/xml';
declare module 'highlight.js/lib/languages/typescript';
declare module 'highlight.js/lib/languages/scss';

import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, LOCALE_ID, inject, provideZoneChangeDetection, ErrorHandler } from '@angular/core';
import { NavigationHistoryService } from '@riise/core/services/nav/navigation-history.service';
import { GlobalErrorHandler } from '@riise/core/services/error-handler/global-error-handler.service';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { routes } from './app.routes';
import { provideHttpClient, withInterceptorsFromDi, HttpClient } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireAuthModule, AngularFireAuth } from '@angular/fire/compat/auth';
import { NgxLoadingModule } from 'ngx-loading';
import { NgxMaskModule } from 'ngx-mask';
import { environment } from '../environments/environment';
import { initializeApp } from 'firebase/app';
import { createApollo } from '@riise/graphql/graphql';
import { HIGHLIGHT_OPTIONS } from 'ngx-highlightjs';
import { FIREBASE_OPTIONS } from '@angular/fire/compat';
import { ErrorInterceptor } from '@riise/core/interceptors/error.interceptor';
import { SpeedTestModule } from 'ng-speed-test';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { LucideAngularModule } from 'lucide-angular';
import { lucideIcons } from '@riise/shared/icons/lucide-icons';
import { NgSelectConfig } from '@ng-select/ng-select';
initializeApp(environment.firebase);


export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    importProvidersFrom(
      AngularFireModule.initializeApp(environment.firebase),
      AngularFireAuthModule,
      NgxLoadingModule.forRoot({}),
      NgxMaskModule.forRoot(),
      SpeedTestModule,
      LucideAngularModule.pick(lucideIcons),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
        }
      })
    ),
    provideApollo(() => {
      const httpLink = inject(HttpLink);
      const afAuth = inject(AngularFireAuth);
      return createApollo(httpLink, afAuth);
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true
    },
    {
      provide: NgSelectConfig,
      useFactory: () => {
        const config = new NgSelectConfig();
        config.appendTo = 'body';
        return config;
      }
    },
    { provide: FIREBASE_OPTIONS, useValue: environment.firebase },
    {
      provide: HIGHLIGHT_OPTIONS,
      useValue: {
        coreLibraryLoader: () => import('highlight.js/lib/core'),
        languages: {
          xml: () => import('highlight.js/lib/languages/xml'),
          typescript: () => import('highlight.js/lib/languages/typescript'),
          scss: () => import('highlight.js/lib/languages/scss'),
        }
      }
    },
    { provide: LOCALE_ID, useValue: 'fr-FR' },
    {
      provide: APP_INITIALIZER,
      useFactory: (navHistory: NavigationHistoryService) => () => navHistory.init(),
      deps: [NavigationHistoryService],
      multi: true
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    }
  ]
};
